import React from 'react'
import { Row, Col, Button } from 'reactstrap'
import { DataGrid } from '@mui/x-data-grid'
import { Chip } from '@mui/material'
import Alert from '@mui/material/Alert'
import { withFirebase } from '../../components/Fb'
import EditButton from '../../components/Buttons/EditButton'
import DeleteButton from '../../components/Buttons/DeleteButton'
import AffiliateCampaignEditor, { CAMPAIGN_STATES } from '../../components/Details/AffiliateCampaignEditor'
import AffiliateCampaignReport from '../../components/Details/AffiliateCampaignReport'
import AffiliateRuleChangeDialog from '../../components/Dialogs/AffiliateRuleChangeDialog'
import AffiliateDeleteDialog from '../../components/Dialogs/AffiliateDeleteDialog'
import { summariseSales } from '../../components/utils/affiliateCommission'
import { allCampaignCodes, fetchSaleSources, rowsForCampaign, campaignCodes } from '../../components/utils/affiliateSales'

const eurFormatter = new Intl.NumberFormat('fi-FI', { style: 'currency', currency: 'EUR' })

const stateLabel = (value) => {
  const found = CAMPAIGN_STATES.find((s) => s.value === value)
  return found ? found.label : value || ''
}

const stateColor = (value) => {
  if (value === 'active') return 'primary'
  if (value === 'paused') return 'warning'
  if (value === 'ended') return 'default'
  return 'default'
}

/**
 * Affiliate campaigns: the list, and the two branches off it (editor and report), following
 * EventTicketsContainer's branch-rather-than-overlay structure.
 *
 * The list shows sales, revenue and commission per campaign, which means it has to price
 * every campaign. The event ticket and activity reads are whole-collection fetches, so they
 * are done ONCE here and shared across campaigns via fetchSaleSources/rowsForCampaign -
 * doing them per campaign would multiply the two biggest reads by the campaign count.
 */
class AffiliateCampaignsContainer extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      campaigns: [],
      affiliates: [],
      summaries: {},
      fetchErrors: [],
      stateFilter: 'all',
      editorOpen: false,
      reportOpen: false,
      selectedCampaign: null,
      ruleChangePayload: null,
      deleteTarget: null,
      working: false,
    }
  }

  componentDidMount() {
    this.updateData()
  }

  updateData = async () => {
    try {
      const [campaigns, affiliates] = await Promise.all([
        this.props.firebase.getAffiliateCampaigns(),
        this.props.firebase.getAffiliates(),
      ])
      this.setState({ campaigns, affiliates, loading: false }, this.priceCampaigns)
    } catch (error) {
      console.error('Affiliate: campaigns failed to load', error)
      this.setState({ campaigns: [], affiliates: [], loading: false })
    }
  }

  // One pass over the shared sources gives every campaign its totals.
  priceCampaigns = async () => {
    const { campaigns } = this.state
    const codes = allCampaignCodes(campaigns)
    if (codes.length === 0) {
      this.setState({ summaries: {}, fetchErrors: [] })
      return
    }
    const sources = await fetchSaleSources(this.props.firebase, codes)
    const summaries = {}
    campaigns.forEach((campaign) => {
      summaries[campaign.id] = summariseSales(rowsForCampaign(sources, campaign), campaign)
    })
    this.setState({ summaries, fetchErrors: sources.errors })
  }

  getFiltered = () => {
    const { campaigns, stateFilter } = this.state
    if (stateFilter === 'all') return campaigns
    return campaigns.filter((c) => (c.state || 'draft') === stateFilter)
  }

  summaryFor = (id) => this.state.summaries[id] || null

  salescountFor = (id) => {
    const summary = this.summaryFor(id)
    return summary ? summary.total.count : 0
  }

  /* Navigation */

  addNewCampaign = () => this.setState({ editorOpen: true, selectedCampaign: null })

  editCampaign = (campaign) => this.setState({ editorOpen: true, reportOpen: false, selectedCampaign: campaign })

  openReport = (campaign) => this.setState({ reportOpen: true, editorOpen: false, selectedCampaign: campaign })

  closeBranches = () => this.setState({ editorOpen: false, reportOpen: false, selectedCampaign: null })

  /* Saving */

  handleSave = async (campaign) => {
    if (campaign.id) {
      await this.props.firebase.updateAffiliateCampaign(campaign)
    } else {
      await this.props.firebase.createAffiliateCampaign(campaign)
    }
    this.setState(
      { editorOpen: false, selectedCampaign: null, ruleChangePayload: null, loading: true },
      this.updateData
    )
  }

  // The editor hands the payload over rather than saving when a rule moved on a campaign
  // that already has sales, so the retroactive effect is confirmed before it happens.
  handleRuleChangeConfirm = (payload) => this.setState({ ruleChangePayload: payload })

  confirmRuleChange = async () => {
    const payload = this.state.ruleChangePayload
    if (!payload) return
    this.setState({ working: true })
    await this.handleSave(payload)
    this.setState({ working: false })
  }

  confirmDelete = async () => {
    const target = this.state.deleteTarget
    if (!target) return
    this.setState({ working: true })
    await this.props.firebase.deleteAffiliateCampaign(target.id)
    this.setState({ working: false, deleteTarget: null, loading: true }, this.updateData)
  }

  /* Grid */

  getColDefinitions = () => [
    { field: 'name', headerName: 'Kampanja', flex: 2, minWidth: 150 },
    { field: 'affiliatename', headerName: 'Kumppani', flex: 1.5, minWidth: 130 },
    {
      field: 'codes',
      headerName: 'Koodit',
      flex: 1.5,
      minWidth: 130,
      sortable: false,
      valueGetter: (value, row) => campaignCodes(row).join(', '),
    },
    {
      field: 'period',
      headerName: 'Voimassa',
      flex: 1.5,
      minWidth: 150,
      sortable: false,
      valueGetter: (value, row) => (row.start || '…') + ' – ' + (row.end || '…'),
    },
    {
      field: 'state',
      headerName: 'Tila',
      width: 110,
      renderCell: (params) => {
        const value = params.row.state || 'draft'
        return (
          <Chip
            size='small'
            label={stateLabel(value)}
            color={stateColor(value)}
            variant={value === 'active' ? 'filled' : 'outlined'}
          />
        )
      },
    },
    {
      field: 'salescount',
      headerName: 'Myyntejä',
      width: 100,
      type: 'number',
      valueGetter: (value, row) => this.salescountFor(row.id),
    },
    {
      field: 'revenue',
      headerName: 'Myynti',
      width: 120,
      type: 'number',
      valueGetter: (value, row) => {
        const summary = this.summaryFor(row.id)
        return summary ? summary.total.gross : 0
      },
      valueFormatter: (value) => eurFormatter.format(Number(value) || 0),
    },
    {
      field: 'commission',
      headerName: 'Palkkio',
      width: 120,
      type: 'number',
      valueGetter: (value, row) => {
        const summary = this.summaryFor(row.id)
        return summary ? summary.total.commission : 0
      },
      valueFormatter: (value) => eurFormatter.format(Number(value) || 0),
    },
    {
      field: 'report',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <Button size='sm' color='info' onClick={() => this.openReport(params.row)}>
          Raportti
        </Button>
      ),
    },
    {
      field: 'edit',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 120,
      renderCell: (params) => <EditButton disablediv onClick={() => this.editCampaign(params.row)} />,
    },
    {
      field: 'delete',
      headerName: ' ',
      sortable: false,
      filterable: false,
      width: 110,
      renderCell: (params) => (
        <DeleteButton disablediv onClick={() => this.setState({ deleteTarget: params.row })} />
      ),
    },
  ]

  renderStateFilters = () => {
    const options = [{ label: 'Kaikki', value: 'all' }, ...CAMPAIGN_STATES]
    return options.map((option) => (
      <Chip
        key={option.value}
        label={option.label}
        clickable
        size='small'
        color='primary'
        variant={this.state.stateFilter === option.value ? 'filled' : 'outlined'}
        onClick={() => this.setState({ stateFilter: option.value })}
        style={{ marginRight: 4 }}
      />
    ))
  }

  renderNoPartners = () => {
    if (this.state.affiliates.length > 0) return null
    return (
      <Alert severity='info' sx={{ mb: 2 }}>
        Lisää ensin kumppani Kumppanit-sivulla — kampanja tarvitsee kumppanin, jolle palkkio maksetaan.
      </Alert>
    )
  }

  renderFetchErrors = () => {
    if (this.state.fetchErrors.length === 0) return null
    return (
      <Alert severity='error' sx={{ mb: 2 }}>
        Osa myynneistä jäi hakematta ({this.state.fetchErrors.join(', ')}). Listan luvut ovat vajaat.
      </Alert>
    )
  }

  render() {
    if (this.state.loading) {
      return <div className='content'>Loading ...</div>
    }

    if (this.state.reportOpen && this.state.selectedCampaign) {
      return (
        <div className='content'>
          <AffiliateCampaignReport
            firebase={this.props.firebase}
            campaign={this.state.selectedCampaign}
            onBack={this.closeBranches}
          />
        </div>
      )
    }

    if (this.state.editorOpen) {
      const selected = this.state.selectedCampaign
      return (
        <div className='content'>
          <AffiliateCampaignEditor
            campaign={selected}
            campaigns={this.state.campaigns}
            affiliates={this.state.affiliates}
            salescount={selected ? this.salescountFor(selected.id) : 0}
            onSave={this.handleSave}
            onRuleChangeConfirm={this.handleRuleChangeConfirm}
            onCancel={this.closeBranches}
          />
          <AffiliateRuleChangeDialog
            open={!!this.state.ruleChangePayload}
            campaign={this.state.ruleChangePayload}
            salescount={selected ? this.salescountFor(selected.id) : 0}
            working={this.state.working}
            onCancel={() => this.setState({ ruleChangePayload: null })}
            onConfirm={this.confirmRuleChange}
          />
        </div>
      )
    }

    return (
      <div className='content'>
        <Row className='mb-2'>
          <Col>
            <Button size='sm' onClick={this.addNewCampaign} disabled={this.state.affiliates.length === 0}>
              Lisää kampanja
            </Button>
          </Col>
        </Row>
        {this.renderNoPartners()}
        {this.renderFetchErrors()}
        <Row>
          <Col className='mb-2'>{this.renderStateFilters()}</Col>
        </Row>
        <Row>
          <Col xs='12'>
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 260px)', backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <DataGrid
                  rows={this.getFiltered()}
                  columns={this.getColDefinitions()}
                  density='compact'
                  initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                  pageSizeOptions={[25, 50, 100]}
                />
              </div>
            </div>
          </Col>
        </Row>
        <AffiliateDeleteDialog
          open={!!this.state.deleteTarget}
          type='campaign'
          name={this.state.deleteTarget ? this.state.deleteTarget.name : ''}
          working={this.state.working}
          onCancel={() => this.setState({ deleteTarget: null })}
          onConfirm={this.confirmDelete}
        />
      </div>
    )
  }
}

export default withFirebase(AffiliateCampaignsContainer)
